import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useStravaErrorLogging } from '@strava/container-context';
import {
  getUniqueSizesFromImages,
  getUniqueFormatsFromImages,
  getUniqueResolutionsFromImages,
  getImagesBySize,
  getImagesByFormat,
  getImagesByResolution
} from './helpers';

const SourceTags = ({ images }) => {
  const { logError } = useStravaErrorLogging();
  const [sources, setSources] = useState([]);

  useEffect(() => {
    const getSourceData = () => {
      const sourceData = [];

      try {
        getUniqueSizesFromImages(images).forEach((size) => {
          // Loop through sizes
          const imagesForSize = getImagesBySize(images, size); // Get images for current size
          const formatsForSize = getUniqueFormatsFromImages(imagesForSize); // Get formats for current sized images

          formatsForSize.forEach((format) => {
            // Loop through formats
            const imagesForSizeAndFormat = getImagesByFormat(
              imagesForSize,
              format
            ); // Get images for current size and format

            const resolutionsForSizeAndFormat = getUniqueResolutionsFromImages(
              imagesForSizeAndFormat
            ); // Get resolutions for current size and format

            // Get srcSet value
            const srcSet = resolutionsForSizeAndFormat
              .map((resolution) => {
                // Map through resolutions
                const imageBySizeAndFormatAndResolution = getImagesByResolution(
                  imagesForSizeAndFormat,
                  resolution
                ); // Get image for current size, format and resolution
                if (imageBySizeAndFormatAndResolution[0]) {
                  return `${imageBySizeAndFormatAndResolution[0].file} ${resolution}`; // Return filename and resolution e.g. '863bfd6a6d3c6e43e7c6.png 1x'
                }
                return false;
              })
              .filter(Boolean) // Strip out any falsy values
              .join(', '); // Concatenate to form srcSet e.g. '863bfd6a6d3c6e43e7c6.png 1x, edbe289c4c8ee72858c6.png 2x'

            sourceData.push({
              size,
              format,
              srcSet
            });
          });
        });
      } catch (error) {
        logError(new Error(`Source tags not created, Error: ${error}`));
      }

      return sourceData;
    };

    const sourceData = getSourceData();
    setSources(sourceData);
  }, [images, logError]);

  return (
    <>
      {sources.map((source) => {
        const { size, format, srcSet } = source;

        return (
          <source
            key={`${size.id}_${format}`}
            data-testid={`${size.id}-${format}`}
            media={`(min-width: ${size.minVw})`}
            srcSet={srcSet}
            type={`image/${format}`}
          />
        );
      })}
    </>
  );
};

SourceTags.propTypes = {
  images: PropTypes.arrayOf(
    PropTypes.shape({
      file: PropTypes.string.isRequired,
      format: PropTypes.oneOf(['png', 'jpg', 'webp']).isRequired,
      resolution: PropTypes.oneOf(['1x', '2x']).isRequired,
      size: PropTypes.shape({
        id: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl']),
        value: PropTypes.string,
        minVw: PropTypes.string
      }).isRequired
    })
  ).isRequired
};

export default SourceTags;
